import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import i18next from 'i18next';

import type {
  IAgent,
  IAgentsCategorized,
  IContract,
  IContractDetails,
  IContractRequirement,
  IContractSchedules,
  IContractState,
  IResignationPending,
  IRestermReason,
  IThunkAPI,
  ITimeOff,
  IUnmetContractRequirement,
} from '@/store/types';
import type {
  IAgentRaw,
  IClientContract,
  IDeleteResignPendingRequest,
  IDeleteTimeOffRequest,
  IGetAdherenceReason,
  IGetAdherenceReasonPayload,
  IGetAgentsAdherenceReasonPayload,
  IGetAgentsContractAdherenceRequest,
  IGetAgentsContractAttendance,
  IGetClientContractRequirements,
  IGetContractAdherence,
  IGetContractAdherenceRequest,
  IGetContractAttendance,
  IGetContractAttendanceRequest,
  IGetContractDetails,
  IReinstateAgentRequest,
  IRestermAgentRequest,
  IRestermAgentResign,
  ISetAdherenceReasonRequest,
  ISetTimeOffRequest,
  ITimeOffRaw,
} from '@/api/types';
import type { RootState } from '@/store';

import { contract as api } from '@/api';
import {
  categorizeAgents,
  getActionPrefixes,
  isActionFromSlice,
  isFulfilledAction,
  isKnownError,
  isPendingAction,
  isRejectedAction,
  logError,
  makeCancellable,
  onFulfilledAction,
  onPendingAction,
  onRejectedAction,
  transformContractAttendanceResponse,
} from '@/helpers';
import { KnownErrors, RequirementType, RestermReasonType, UserType } from '@/store/enums';
import {
  transformAgents,
  transformClientContracts,
  transformContractDetails,
  transformContractRequirements,
  transformContractRequirementsUnmet,
  transformContracts,
  transformContractSchedules,
  transformContractSchedulesFromRequirements,
  transformResignPendingContracts,
  transformRestremReasons,
  transformTimeOffsList,
} from '@/api/transforms';
import { baseCategorizedAgents } from '@/constants/client';
import { SLICES } from '@/constants/actions';

const initialState: IContractState = {
  contractSchedules: null,
  contractRequirementsAll: null,
  contractRequirementsUnmet: [],
  contractAttendance: [],
  contractAdherence: {},
  contractAdherenceReason: [],
  contractResignationPendingList: [],
  contractsList: null,
  contractDetails: null,
  client: {
    contractsList: [],
    contractRequirementsAll: [],
    contractSchedules: {},
    contractDetails: null,
    contractAttendance: [],
    contractAdherence: [],
    contractAdherenceReason: [],
    contractAgents: null,
    restermReasonsList: null,
    currentAgent: {
      history: null,
      active: null,
    },
  },
  restermReasonsList: [],
  resignContractModalData: {
    stepOne: {
      agentContractId: 0,
      agentPositionId: 0,
      agentContractAssnStartBlock: 0,
      agentContractResTermId: 0,
      effectiveBlock: 0,
      resTermReasonId: 0,
      reasonText: '',
    },
    stepTwo: {
      2: '',
      3: '',
      6: '',
      7: '',
      8: '',
      9: '',
      10: '',
      13: '',
      14: '',
      15: '',
      16: '',
    },
    isStepOneValid: false,
    isStepTwoValid: false,
  },
  baseHours: [],
  peakHours: [],
  timeOffsList: [],
  timeOffsRaw: [],
  meta: {
    pending: [],
  },
};

const { spPrefix, suPrefix, defaultPrefix } = getActionPrefixes(SLICES.CONTRACT);

/**
 * Common
 */

export const setAdherenceReason = createAsyncThunk<unknown, ISetAdherenceReasonRequest, IThunkAPI>(
  `${defaultPrefix}/adherence/reason/set`,
  async (payload, thunkAPI) => {
    try {
      const userType = (thunkAPI.getState() as RootState).auth.menuType ?? UserType.unauthorized;
      if (![UserType.client, UserType.agent].includes(userType))
        throw new Error('Set adherence reason called by wrong user type.');
      const response =
        userType === UserType.agent
          ? await api.setAdherenceReason(payload)
          : await api.setClientRestermReasons(payload);

      if (isKnownError(response)) return;
      userType === UserType.client
        ? await thunkAPI.dispatch(
            getAgentsContractAdherenceReason({
              weekId: payload.selectedWeekId,
              contractId: payload.contractId,
              userId: payload.userId,
            }),
          )
        : await thunkAPI.dispatch(
            getAdherenceReason({ weekId: payload.selectedWeekId, contractId: payload.contractId }),
          );

      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response || e.message);
    }
  },
);

/**
 * SP
 */

export const getContractRequirementsAll = createAsyncThunk<IContractRequirement[], undefined, IThunkAPI>(
  `${spPrefix}/requirements/all/list`,
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const getContractRequirementsAllRequest = makeCancellable(api.getContractRequirementsAll, thunkAPI.signal);
      const response = await getContractRequirementsAllRequest();
      return transformContractRequirements(response.data, state.auth.user?.timeZoneName);
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getContractRequirementsUnmet = createAsyncThunk<
  IUnmetContractRequirement[],
  { startWeekId: number; endWeekId: number },
  IThunkAPI
>(`${spPrefix}/requirements/unmet/list`, async (payload, thunkAPI) => {
  try {
    const getContractRequirementsUnmetRequest = makeCancellable(api.getContractRequirementsUnmet, thunkAPI.signal);
    const response = await getContractRequirementsUnmetRequest(payload);

    if (isKnownError(response.data)) throw response.data;

    return transformContractRequirementsUnmet(response.data);
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response);
  }
});

export const getTimeOffs = createAsyncThunk<{ list: ITimeOff[]; raw: ITimeOffRaw[] }, undefined, IThunkAPI>(
  `${spPrefix}/timeoff/get`,
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel('getTimeOffs cancelled');
      });
      const response = await api.getTimeOffs({ cancelToken: source.token });
      const raw = response.data;
      const list = transformTimeOffsList(raw, state.auth.user?.timeZoneName);

      return {
        raw,
        list,
      };
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const setTimeOff = createAsyncThunk<unknown, ISetTimeOffRequest, IThunkAPI>(
  `${spPrefix}/timeoff/create`,
  async (payload, thunkAPI) => {
    try {
      const response = await api.setTimeOff(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const deleteTimeOff = createAsyncThunk<unknown, IDeleteTimeOffRequest, IThunkAPI>(
  `${spPrefix}/timeoff/set`,
  async (payload, thunkAPI) => {
    try {
      const response = await api.deleteTimeOff(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getContractAttendance = createAsyncThunk<
  IGetContractAttendance[],
  IGetContractAttendanceRequest,
  IThunkAPI
>(`${spPrefix}/attendance`, async (payload, thunkAPI) => {
  try {
    const source = axios.CancelToken.source();
    thunkAPI.signal.addEventListener('abort', () => {
      source.cancel('getContractAttendance cancelled');
    });
    const response = await api.getContractAttendance(payload, {
      cancelToken: source.token,
    });
    if (response.data === KnownErrors.ItemNotFound) return [];
    if (typeof response.data === 'string') throw response.data;
    return transformContractAttendanceResponse(response.data);
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response || e.message);
  }
});

export const getContractAdherence = createAsyncThunk<IGetContractAdherence[], IGetContractAdherenceRequest, IThunkAPI>(
  `${spPrefix}/adherence`,
  async (payload, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel('getContractAdherence cancelled');
      });

      const response = await api.getContractAdherence(payload, { cancelToken: source.token });
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response || e.message);
    }
  },
);

export const getAdherenceReason = createAsyncThunk<IGetAdherenceReason[], IGetAdherenceReasonPayload, IThunkAPI>(
  `${spPrefix}/adherence/reason/get`,
  async (payload, thunkAPI) => {
    try {
      const getAdherenceReason = makeCancellable(api.getAdherenceReason, thunkAPI.signal);
      const response = await getAdherenceReason(payload);
      if (typeof response.data === 'string') return [];

      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response || e.message);
    }
  },
);

export const getAgentContractsList = createAsyncThunk<IContract[], undefined, IThunkAPI>(
  `${spPrefix}/contract/list`,
  async (_, thunkAPI) => {
    try {
      const getAgentContractList = makeCancellable(api.getAgentContractActiveList, thunkAPI.signal);
      const response = await getAgentContractList();
      const { data } = response;
      if (typeof data === 'string') throw data;
      return transformContracts({
        contracts: data,
        contractsPending: (thunkAPI.getState() as RootState).contract.contractResignationPendingList,
      });
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getAgentContractListWithResignations = createAsyncThunk<
  { contracts: IContract[]; pendingResigns: IResignationPending[] },
  undefined,
  IThunkAPI
>(`${spPrefix}/resignations/list`, async (_, thunkAPI) => {
  try {
    const getAgentContractList = makeCancellable(api.getAgentContractAllList, thunkAPI.signal);
    const { data: contractsList } = await getAgentContractList();
    if (typeof contractsList === 'string') throw contractsList;

    const sourceResignList = axios.CancelToken.source();
    thunkAPI.signal.addEventListener('abort', () => {
      sourceResignList.cancel('getResignationPendingList cancelled');
    });
    const response = await api.getResignationPendingList({ cancelToken: sourceResignList.token });
    const parsedResignPendingList = transformResignPendingContracts(response.data);

    return {
      contracts: transformContracts({
        contracts: contractsList,
        contractsPending: parsedResignPendingList,
      }),
      pendingResigns: parsedResignPendingList,
    };
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response);
  }
});

export const getRestermReasonsList = createAsyncThunk<IRestermReason[], undefined, IThunkAPI>(
  `${spPrefix}/resterm/reasons/list`,
  async (_, thunkAPI) => {
    try {
      const getRestermReasons = makeCancellable(api.getRestermReasons, thunkAPI.signal);
      const response = await getRestermReasons();
      if (isKnownError(response.data)) return [];

      return transformRestremReasons(response.data, RestermReasonType.resignation);
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const upsertResignContract = createAsyncThunk<number, IRestermAgentResign, IThunkAPI>(
  `${spPrefix}/contract/resign`,
  async (payload, thunkAPI) => {
    try {
      const response = await api.upsertResignContract(payload);

      if (isKnownError(response.data)) throw response.data;
      await thunkAPI.dispatch(clearResignContractModalData());
      await thunkAPI.dispatch(getAgentContractListWithResignations());
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const cancelResignContract = createAsyncThunk<boolean, IDeleteResignPendingRequest, IThunkAPI>(
  `${spPrefix}/resign/cancel`,
  async (payload, thunkAPI) => {
    try {
      const response = await api.cancelResignContract(payload);

      await thunkAPI.dispatch(getAgentContractListWithResignations());
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getContractDetails = createAsyncThunk<
  { details: IContractDetails; schedules: IContractSchedules },
  IGetContractDetails,
  IThunkAPI
>(`${spPrefix}/details`, async (payload, thunkAPI) => {
  try {
    const getContractDetails = makeCancellable(api.getContractDetails, thunkAPI.signal);
    const { data } = await getContractDetails(payload);
    if (typeof data === 'string') throw data;
    const details = transformContractDetails(data);
    const contractSchedules = transformContractSchedules(data);
    return { details, schedules: { [payload.contractId]: contractSchedules } };
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response);
  }
});

/**
 * SU
 */

const _getContractAgentList = async (
  payload: { contractId: number; text?: string },
  signal: AbortSignal,
  state: RootState,
): Promise<{ agents: IAgentRaw[] | KnownErrors; restermReasons: IRestermReason[] }> => {
  const getContractsAgentList = makeCancellable(api.getContractsAgentList, signal);

  let restermReasons: IRestermReason[] | null = state.contract.client.restermReasonsList;

  if (!restermReasons) {
    const getClientRestermReasons = makeCancellable(api.getClientRestermReasons, signal);

    const { data: rawReasons } = await getClientRestermReasons();
    const restermReasonsRaw = typeof rawReasons === 'string' ? [] : rawReasons;
    restermReasons = transformRestremReasons(restermReasonsRaw);
  }

  const { data } = await getContractsAgentList(payload);
  return { restermReasons, agents: data };
};

export const getAgentsContractList = createAsyncThunk<IContract[], { userId: number }, IThunkAPI>(
  `${suPrefix}/agent/contracts/list`,
  async (payload, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel('getSuAgentContractList cancelled');
      });
      const { data } = await api.getAgentsContractList(payload, { cancelToken: source.token });
      if (isKnownError(data)) throw data;
      return transformContracts({
        contracts: data,
        contractsPending: [],
      });
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getClientContractRequirementsAll = createAsyncThunk<
  { requirements: IContractRequirement[]; contractSchedules: IContractSchedules },
  IGetClientContractRequirements | undefined,
  IThunkAPI
>(`${suPrefix}/requirements/list`, async (payload, thunkAPI) => {
  try {
    const state = thunkAPI.getState() as RootState;
    const source = axios.CancelToken.source();
    thunkAPI.signal.addEventListener('abort', () => {
      source.cancel('getClientContractRequirementsAll cancelled');
    });
    const { data } = await api.getClientRequirementsList(payload ?? {}, { cancelToken: source.token });
    if (typeof data === 'string') throw data;

    const requirements = transformContractRequirements(data, state.auth.user?.timeZoneName);

    const contractSchedules = transformContractSchedulesFromRequirements(requirements);
    return { requirements, contractSchedules };
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response);
  }
});

export const getClientContractsList = createAsyncThunk<IClientContract[], undefined, IThunkAPI>(
  `${suPrefix}/list`,
  async (_, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel('getContractsList cancelled');
      });
      const clientId = (thunkAPI.getState() as RootState).auth.user?.currentClient?.clientId;
      const response = await api.getClientContractList({ clientId }, { cancelToken: source.token });
      const { data } = response;
      if (typeof data === 'string') throw data;
      const dataFiltred = data.filter(x => x.isActive === true);
      return transformClientContracts({ clientContracts: dataFiltred });
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getContractsAgentList = createAsyncThunk<
  { agents: IAgentsCategorized; restermReasons: IRestermReason[] },
  { contractId: number; text: string },
  IThunkAPI
>(`${suPrefix}/agents`, async (payload, thunkAPI) => {
  try {
    const { restermReasons, agents: agentsRaw } = await _getContractAgentList(
      payload,
      thunkAPI.signal,
      thunkAPI.getState() as RootState,
    );
    if (typeof agentsRaw === 'string') return { agents: baseCategorizedAgents(), restermReasons };

    const agents = transformAgents(agentsRaw, restermReasons);
    return { agents: categorizeAgents(agents), restermReasons };
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response);
  }
});

export const getAgentContractHistory = createAsyncThunk<IAgent[], { contractId: number; userId: number }, IThunkAPI>(
  `${suPrefix}/agent/history`,
  async (payload, thunkAPI) => {
    const { restermReasons, agents } = await _getContractAgentList(
      payload,
      thunkAPI.signal,
      thunkAPI.getState() as RootState,
    );
    if (typeof agents === 'string') return [];

    return transformAgents(agents, restermReasons).filter(item => item.userId === payload.userId);
  },
);

export const getClientContractDetails = createAsyncThunk<
  { details: IContractDetails | null; schedules: IContractSchedules | null },
  IGetContractDetails,
  IThunkAPI
>(`${suPrefix}/details`, async (payload, thunkAPI) => {
  try {
    const { data } = await makeCancellable(api.getClientContractDetails, thunkAPI.signal)(payload);
    if (isKnownError(data)) return { details: {} as IContractDetails, schedules: {} as IContractSchedules };

    const details = transformContractDetails(data);
    const contractSchedules = transformContractSchedules(data);
    return { details, schedules: { [payload.contractId]: contractSchedules } };
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response);
  }
});

export const getAgentsContractAttendance = createAsyncThunk<
  IGetContractAttendance[],
  IGetAgentsContractAttendance,
  IThunkAPI
>(`${suPrefix}/attendance`, async (payload, thunkAPI) => {
  try {
    const getAgentsContractAttendance = makeCancellable(api.getAgentsContractAttendance, thunkAPI.signal);
    const { data } = await getAgentsContractAttendance(payload);
    if (isKnownError(data)) return [];
    return transformContractAttendanceResponse(data);
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response || e.message);
  }
});

export const getAgentsContractAdherence = createAsyncThunk<
  IGetContractAdherence[],
  IGetAgentsContractAdherenceRequest,
  IThunkAPI
>(`${suPrefix}/adherence`, async (payload, thunkAPI) => {
  try {
    const getAgentsContractAdherence = makeCancellable(api.getAgentsContractAdherence, thunkAPI.signal);
    const { data } = await getAgentsContractAdherence(payload);
    if (isKnownError(data)) return [];
    // TODO update when backend will flat it on their side
    return data.flat();
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response || e.message);
  }
});

export const getAgentsContractAdherenceReason = createAsyncThunk<
  IGetAdherenceReason[],
  IGetAgentsAdherenceReasonPayload,
  IThunkAPI
>(`${suPrefix}/adherence/reason/list`, async (payload, thunkAPI) => {
  try {
    const getAgentsContractAdherenceReason = makeCancellable(api.getAgentsContractAdherenceReason, thunkAPI.signal);

    const response = await getAgentsContractAdherenceReason(payload);
    if (isKnownError(response.data)) return [];

    return response.data;
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response || e.message);
  }
});

export const getClientRestermReasonsList = createAsyncThunk<IRestermReason[], undefined, IThunkAPI>(
  `${suPrefix}/resterm/reason/vocab`,
  async (_, thunkAPI) => {
    try {
      const getClientRestermReasonsList = makeCancellable(api.getClientRestermReasons, thunkAPI.signal);
      const { data } = await getClientRestermReasonsList(undefined);
      if (isKnownError(data)) return [];
      return transformRestremReasons(data, RestermReasonType.termination_and_suspension);
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const removeAgent = createAsyncThunk<
  { agents: IAgentsCategorized; restermReasons: IRestermReason[] },
  IRestermAgentRequest,
  IThunkAPI
>(`${suPrefix}/resterm/remove`, async (payload, thunkAPI) => {
  try {
    const removeAgent = makeCancellable(api.removeAgent, thunkAPI.signal);
    const { data } = await removeAgent(payload);
    if (isKnownError(data)) throw data;
    const { agents, restermReasons } = await _getContractAgentList(
      { contractId: payload.contractId },
      thunkAPI.signal,
      thunkAPI.getState() as RootState,
    );
    if (isKnownError(agents)) throw agents;

    const parsedAgents = transformAgents(agents, restermReasons);

    return { agents: categorizeAgents(parsedAgents), restermReasons };
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response || e.message || e);
  }
});

export const suspendAgent = createAsyncThunk<
  { agents: IAgentsCategorized; restermReasons: IRestermReason[] },
  IRestermAgentRequest,
  IThunkAPI
>(`${suPrefix}/resterm/suspend`, async (payload, thunkAPI) => {
  try {
    const suspendAgent = makeCancellable(api.suspendAgent, thunkAPI.signal);
    const { data } = await suspendAgent(payload);
    if (isKnownError(data)) throw data;
    const { agents, restermReasons } = await _getContractAgentList(
      { contractId: payload.contractId },
      thunkAPI.signal,
      thunkAPI.getState() as RootState,
    );
    if (isKnownError(agents)) throw agents;

    const parsedAgents = transformAgents(agents, restermReasons);

    return { agents: categorizeAgents(parsedAgents), restermReasons };
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response || e.message);
  }
});

export const reinstateAgent = createAsyncThunk<
  { agents: IAgentsCategorized; restermReasons: IRestermReason[] },
  IReinstateAgentRequest,
  IThunkAPI
>(`${suPrefix}/resterm/reinstate`, async (payload, thunkAPI) => {
  try {
    const reinstateAgent = makeCancellable(api.reinstateAgent, thunkAPI.signal);
    const { data } = await reinstateAgent(payload);
    if (isKnownError(data)) throw data;
    const { agents, restermReasons } = await _getContractAgentList(
      { contractId: payload.contractId },
      thunkAPI.signal,
      thunkAPI.getState() as RootState,
    );
    if (isKnownError(agents)) throw agents;

    const parsedAgents = transformAgents(agents, restermReasons);

    return { agents: categorizeAgents(parsedAgents), restermReasons };
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response || e.message);
  }
});

export const slice = createSlice({
  name: 'contract',
  initialState,
  reducers: {
    setResignContractStepOne(state, action) {
      state.resignContractModalData.stepOne = action.payload;
      state.resignContractModalData.isStepOneValid = true;
    },
    setResignContractStepTwo(state, action) {
      state.resignContractModalData.stepTwo = action.payload;
      state.resignContractModalData.isStepTwoValid = true;
    },
    clearResignContractModalData(state) {
      state.resignContractModalData.isStepOneValid = false;
      state.resignContractModalData.isStepTwoValid = false;
      state.resignContractModalData.stepOne = initialState.resignContractModalData.stepOne;
      state.resignContractModalData.stepTwo = initialState.resignContractModalData.stepTwo;
    },
    clearContractRequirements(state) {
      state.contractRequirementsAll = [];
      state.contractRequirementsUnmet = [];
      state.baseHours = [];
      state.peakHours = [];
    },
    clearTimeOffs(state) {
      state.timeOffsList = [];
    },
    clearState(state) {
      state.meta.pending = [];
    },
    clearAdheranceReaons(state) {
      state.contractAdherenceReason = [];
    },
  },
  extraReducers: builder => {
    builder
      //SP
      .addCase(getContractRequirementsAll.fulfilled, (state, action) => {
        state.contractRequirementsAll = action.payload;
        state.baseHours = action.payload.flatMap(requirement => {
          const bases = requirement.contractRequirements.find(item => item.requirementType === RequirementType.base);
          return bases ? { ...bases, ...requirement.clientSummary } : [];
        });
        state.peakHours = action.payload.flatMap(requirement => {
          const peaks = requirement.contractRequirements.find(item => item.requirementType === RequirementType.peak);
          return peaks ? { ...peaks, ...requirement.clientSummary } : [];
        });
      })
      .addCase(getContractRequirementsUnmet.fulfilled, (state, action) => {
        state.contractRequirementsUnmet = action.payload;
      })
      .addCase(getContractAttendance.fulfilled, (state, action) => {
        state.contractAttendance = action.payload;
      })
      .addCase(getContractAdherence.fulfilled, (state, action) => {
        state.contractAdherence = { ...state.contractAdherence, [action.meta.arg.contractId]: action.payload };
      })
      .addCase(getAdherenceReason.fulfilled, (state, action) => {
        state.contractAdherenceReason = action.payload;
      })
      .addCase(getAgentContractsList.fulfilled, (state, action) => {
        state.contractsList = action.payload;
      })
      .addCase(getAgentContractListWithResignations.fulfilled, (state, action) => {
        state.contractsList = action.payload.contracts;
        state.contractResignationPendingList = action.payload.pendingResigns;
      })
      .addCase(getTimeOffs.fulfilled, (state, action) => {
        state.timeOffsList = action.payload.list;
        state.timeOffsRaw = action.payload.raw;
      })
      .addCase(getRestermReasonsList.fulfilled, (state, action) => {
        state.restermReasonsList = action.payload;
      })
      .addCase(cancelResignContract.rejected, () => {
        toast.warning(i18next.t`common.somethingWentWrong`);
      })
      .addCase(getContractDetails.fulfilled, (state, action) => {
        state.contractDetails = action.payload.details;
        state.contractSchedules = action.payload.schedules;
      })
      //SU
      .addCase(getClientContractRequirementsAll.fulfilled, (state, action) => {
        state.client.contractRequirementsAll = action.payload.requirements;
        state.client.contractSchedules = action.payload.contractSchedules; //TODO remove when LVXC-640 is done
      })
      .addCase(getClientContractsList.fulfilled, (state, action) => {
        state.client.contractsList = action.payload;
      })
      .addCase(getClientContractDetails.fulfilled, (state, action) => {
        state.client.contractSchedules = action.payload.schedules;
      })
      .addCase(getContractsAgentList.fulfilled, (state, action) => {
        state.client.contractAgents = action.payload.agents;
        state.client.restermReasonsList = action.payload.restermReasons;
      })
      .addCase(getAgentsContractAttendance.fulfilled, (state, action) => {
        state.client.contractAttendance = action.payload;
      })
      .addCase(getAgentsContractAdherence.fulfilled, (state, action) => {
        state.client.contractAdherence = action.payload;
      })
      .addCase(getAgentsContractAdherenceReason.fulfilled, (state, action) => {
        state.client.contractAdherenceReason = action.payload;
      })
      .addCase(getClientRestermReasonsList.fulfilled, (state, action) => {
        state.client.restermReasonsList = action.payload;
      })
      .addCase(reinstateAgent.fulfilled, (state, action) => {
        state.client.contractAgents = action.payload.agents;
      })
      .addCase(suspendAgent.fulfilled, (state, action) => {
        state.client.contractAgents = action.payload.agents;
      })
      .addCase(removeAgent.fulfilled, (state, action) => {
        state.client.contractAgents = action.payload.agents;
      })
      .addCase(getAgentContractHistory.fulfilled, (state, action) => {
        state.client.currentAgent.history = action.payload;
        state.client.currentAgent.active = action.payload.find(item => item.type === 'current') ?? null;
      })
      //META
      .addMatcher(action => isFulfilledAction(action) && isActionFromSlice(action, SLICES.CONTRACT), onFulfilledAction)
      .addMatcher(action => isRejectedAction(action) && isActionFromSlice(action, SLICES.CONTRACT), onRejectedAction)
      .addMatcher(action => isPendingAction(action) && isActionFromSlice(action, SLICES.CONTRACT), onPendingAction);
  },
});

// actions
export const {
  clearState,
  clearContractRequirements,
  setResignContractStepOne,
  setResignContractStepTwo,
  clearResignContractModalData,
  clearTimeOffs,
} = slice.actions;

export default slice.reducer;
